import { useCallback, useEffect, useRef } from 'react';

import { useSocket } from './useSocket';

/**
 * Plays a brief WhatsApp-style two-tone "ding" on every incoming
 * `inbox:new_message` event. Synthesized via the Web Audio API so we
 * don't ship a binary asset — keeps the bundle clean and avoids the
 * extra HTTP round-trip on first message.
 *
 * Audio context is created lazily (most browsers require a user
 * gesture before any AudioContext can output sound — by the time a
 * message arrives, the user has almost always already clicked
 * something on the page, so this works in practice). If the very
 * first message lands before any interaction, the play silently
 * fails — acceptable trade-off.
 *
 * Mount once at the app root (AppLayout) so the sound triggers on
 * every page, not just the inbox.
 */
export function useInboundMessageSound(): void {
  const { on, off } = useSocket();
  const audioCtxRef = useRef<AudioContext | null>(null);

  const getCtx = useCallback((): AudioContext | null => {
    if (audioCtxRef.current) return audioCtxRef.current;
    const Ctor =
      (window as unknown as { AudioContext?: typeof AudioContext }).AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    audioCtxRef.current = new Ctor();
    return audioCtxRef.current;
  }, []);

  // Emit the two-tone ding against the context's *current* clock. Must be
  // called only when the context is running, otherwise the schedule lands
  // on a frozen clock and never sounds.
  const schedule = useCallback((ctx: AudioContext) => {
    const now = ctx.currentTime;
    // Two-tone ding: 880 Hz then 660 Hz, ~250 ms total.
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.setValueAtTime(660, now + 0.12);
    gain.gain.setValueAtTime(0.0, now);
    gain.gain.linearRampToValueAtTime(0.18, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
    osc.start(now);
    osc.stop(now + 0.28);
  }, []);

  const playDing = useCallback(() => {
    try {
      const ctx = getCtx();
      if (!ctx) return;
      // Browsers auto-suspend the AudioContext when the tab is backgrounded
      // (and on the autoplay-policy gate before any gesture). Resume first so
      // the ding rings even when the window is not focused, then schedule on
      // the now-running clock.
      if (ctx.state === 'suspended') {
        void ctx
          .resume()
          .then(() => schedule(ctx))
          .catch(() => {});
      } else {
        schedule(ctx);
      }
    } catch {
      // Audio device unavailable. Silent skip — the badge + page refresh
      // still surface the new message visually.
    }
  }, [getCtx, schedule]);

  // Unlock the context on the first user gesture so it is already "running"
  // by the time a message arrives. A context unlocked by a gesture can be
  // resumed programmatically later — including while the tab is in the
  // background — which is what lets the ding play out of focus.
  useEffect(() => {
    const unlock = (): void => {
      const ctx = getCtx();
      if (ctx && ctx.state === 'suspended') void ctx.resume().catch(() => {});
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [getCtx]);

  useEffect(() => {
    on('inbox:new_message', playDing);
    return () => {
      off('inbox:new_message', playDing);
    };
  }, [on, off, playDing]);
}
